<template>
  <v-container class="h-full">
    <div class="flex justify-between items-center px-4 py-4">
      <div class="flex gap-4 items-center">
        <v-btn
          color="primary"
          @click="$router.push({ name: 'group-create' })"
        >
          Create Group
        </v-btn>
        <v-menu
          offset="8"
          :disabled="loading"
        >
          <template #activator="{ props }">
            <div class="d-flex justify-center">
              <v-btn
                v-bind="props"
                size="32"
                color="primary"
                icon="mdi-dots-horizontal"
              />
            </div>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-title class="!text-sm cursor-pointer" @click="exportCSV">
                Export CSV...
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <v-text-field
        v-model="search"
        label="Search"
        prepend-inner-icon="mdi-magnify"
        class="max-w-sm"
        clearable
        density="compact"
      />
    </div>
    <v-data-table-server
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      v-model:sort-by="sortBy"
      :headers="headers"
      :items="items"
      :items-length="itemsLength"
      :items-per-page-options="[10, 25, 50, 100]"
      :loading="loading"
      :search="search"
    >
      <template #item.name="{ item }">
        <p class="pt-2 font-bold text-base" v-text="item.name || ''" />
        <p class="text-sm opacity-35 pb-2 mb-auto" v-text="item.id" />
      </template>
      <!--
        * Hidden until listAgencyData starts passing this data
        <template #item.address="{ item }">
        <p v-text="formatValue(item.metadata?.mailingAddress, addressFormatter)" />
        </template>
      -->
      <template #item.globalExternalId="{ item }">
        <p v-text="item.globalExternalId" />
      </template>
      <template #item.programAssociations="{ item }">
        <p
          class="whitespace-pre py-2 min-h-full flex items-center"
          v-text="formatExternalInfo(item)"
        />
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex justify-center py-4">
          <v-btn
            variant="flat"
            size="32"
            color="primary"
            icon="mdi-pencil"
            @click="viewGroup(item)"
          />
        </div>
      </template>
    </v-data-table-server>
  </v-container>
</template>

<script lang="ts">
import { Formatter } from '@policyfly/protobuf/pfschema'
import { computed, defineComponent, markRaw, ref, shallowRef, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useApiStore } from '@/stores/api'
import { useDomainStore } from '@/stores/domain'
import { useEnvironmentStore } from '@/stores/environment'

import { RouteNames } from '@/router'
import { bingoDomainToInternalDomain } from '@/utils/domain'
import { bingoEnvironmentToInternalEnvironment } from '@/utils/environment'
import { formatValue } from '@/utils/formatter'

import type { FetchAgencyDataResponse_Agency as Agency } from '@policyfly/protobuf/pfinternal'
import type { DataTableHeader } from 'vuetify'
import type { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort.mjs'

export default defineComponent({
  name: 'GroupList',

  setup () {
    // groups
    const router = useRouter()
    const apiStore = useApiStore()
    const domainStore = useDomainStore()
    const envStore = useEnvironmentStore()

    const internalEnv = computed(() => bingoEnvironmentToInternalEnvironment(envStore.currentEnvironment))
    const internalDomain = computed(() => bingoDomainToInternalDomain(domainStore.currentDomain))
    const groups = shallowRef<Agency[]>([])
    const loading = ref(true)
    async function loadGroups (): Promise<void> {
      try {
        loading.value = true
        groups.value = []
        const response = await apiStore.listAgencyData({
          environment: internalEnv.value,
          domain: internalDomain.value,
        })
        groups.value = response.agencies
        loading.value = false
      } catch (error) {
        console.error('Failed to load groups:', error)
        loading.value = false
      }
    }
    watch([internalEnv, internalDomain], () => {
      if (internalEnv.value && internalDomain.value) loadGroups()
    }, { immediate: true })

    // table
    const headers: DataTableHeader<Agency>[] = markRaw([
      { title: 'Name', key: 'name', value: 'name', sortable: true },
      // Hidden until listAgencyData starts passing address info
      // { title: 'Address', key: 'address', value: 'metadata.mailingAddress', sortable: false },
      { title: 'Global External ID', key: 'globalExternalId', value: 'globalExternalId' },
      { title: 'Program Access', key: 'programAssociations', value: 'programAssociations', sortable: false },
      { title: 'Actions', key: 'actions', value: 'actions', sortable: false, align: 'center' },
    ])
    const search = ref('')
    watch(search, () => {
      page.value = 1
    })
    const addressFormatter = Formatter.create({
      name: {
        oneofKind: 'address',
        address: {},
      },
    })

    // items
    const itemsPerPage = ref(10)
    const page = ref(1)
    const sortBy = ref<SortItem[]>([])
    const filteredItems = computed(() => {
      if (!search.value) return groups.value
      return groups.value.filter((group) => {
        return Object.values(group).some((val) =>
          String(val).toLowerCase().includes(search.value.toLowerCase()),
        )
      })
    })
    const itemsLength = computed(() => filteredItems.value.length)
    const sortedItems = computed(() => {
      return filteredItems.value.slice().sort((a, b) => {
        for (const sortItem of sortBy.value) {
          const aValue = String(a[sortItem.key as keyof typeof a])
          const bValue = String(b[sortItem.key as keyof typeof b])
          if (aValue < bValue) return sortItem.order === 'desc' ? 1 : -1
          if (aValue > bValue) return sortItem.order === 'desc' ? -1 : 1
        }
        return 0
      })
    })
    const items = computed(() => {
      const offset = (page.value - 1) * itemsPerPage.value
      return sortedItems.value.slice(offset, offset + itemsPerPage.value)
    })

    function formatExternalInfo (group: Agency, separator: string = '\n'): string {
      return group.programAssociations
        .filter(({ hasAccess }) => hasAccess)
        .map(({ slug, externalId }) => `${slug.toUpperCase()}${externalId ? `: ${externalId}` : ''}`)
        .sort()
        .join(separator)
    }

    // actions
    function viewGroup (group: Agency): void {
      router.push({ name: RouteNames.GROUP_EDIT, params: { id: group.id } })
    }

    function exportCSV (): void {
      // External Info generates one row per id, or NULL if there aren’t any entries
      const csvRows: string[] = []
      const csvHeaders = ['Name', 'UUID', 'External ID', 'Program Access']
      csvRows.push(csvHeaders.join(','))
      for (const group of filteredItems.value) {
        const baseRow = [
          `"${group.name || ''}"`,
          `"${group.id}"`,
          `"${group.globalExternalId || ''}"`,
          // Hidden until listAgencyData starts passing address info
          // `"${formatValue(group.metadata?.mailingAddress, addressFormatter)}"`,
        ]
        const externalIds = group.programAssociations
          .filter(({ hasAccess }) => hasAccess)
          .map(({ slug, externalId }) => ({ slug, externalId }))
        if (externalIds.length === 0) {
          const row = [...baseRow, '']
          csvRows.push(row.join(','))
        } else {
          for (const { slug, externalId } of externalIds) {
            const row = [...baseRow, `"${slug.toUpperCase()}: ${externalId || 'NULL'}"`]
            csvRows.push(row.join(','))
          }
        }
      }
      // Create and download the CSV file
      const csvContent = csvRows.join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', 'groups.csv')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    return {
      formatValue,
      // groups
      loading,
      // table
      headers,
      search,
      addressFormatter,
      // items
      itemsPerPage,
      page,
      sortBy,
      itemsLength,
      items,
      formatExternalInfo,
      // actions
      viewGroup,
      exportCSV,
    }
  },
})
</script>

<style lang="scss" scoped>
:deep(.v-data-table td) {
  vertical-align: top !important;
}
</style>
